import React, { useEffect, useMemo, useState } from "react";
import { useFormikContext, FieldArray } from "formik";
import axios from "axios";
import { PlusOutlined } from "@ant-design/icons";
import { Button, Select, Tag } from "antd";
import trans from "../../../utils";
import {
  Input,
  TextAreaInput,
  DatePickerInput,
} from "../../Helpers/Input/Input";
import { InputSelect } from "../../Helpers/Input/RefactoredInput";
import {
  useGetModelsByProductQuery,
  useGetIssuesByModelQuery,
  useGetSparePartsByIssuesQuery,
  useGetVillagesByMarkazQuery,
} from "../../../Redux/service/CallCenter/CallCenter";
import { useGetAllSkusQuery } from "../../../Redux/service/Sku/Sku";
import { EgyptGovernorates } from "../../../constants";
import { getMarkazOptions, getGovernorateCenter } from "../../../utils/CountryState/egyptMarkaz";
import CallCenterMapPicker from "./CallCenterMapPicker";
import ContactNumberList from "../ContactNumberList/ContactNumberList";
import { COMPLAINT_DESCRIPTION_MAX_LENGTH, MAX_SECONDARY_PHONES } from "./callCenterValidation";
import CreateIssueModal from "./CreateIssueModal";
import CreateSparePartModal from "./CreateSparePartModal";
import CreateVillageModal from "./CreateVillageModal";
import {
  SERVICE_REQUEST_COLORS,
  getLocalizedColorLabel,
} from "./serviceRequestColors";

const DATE_FORMAT = "YYYY-MM-DD HH:mm:ss";
const NOMINATIM_SEARCH_URL = "https://nominatim.openstreetmap.org/search";

// Governorate → Markaz → Village cascade zoom levels — same as the create
// form's CallCenterFormBody; a manual map click keeps whichever zoom the
// cascade last landed on.
const ZOOM_GOVERNORATE = 9;
const ZOOM_MARKAZ = 12;
const ZOOM_VILLAGE = 15;

// Village/neighborhood has no dataset in this codebase — forward geocode is
// the only option at that granularity (same as the create form).
const forwardGeocode = async (query, limit = 1) => {
  try {
    const { data } = await axios.get(NOMINATIM_SEARCH_URL, {
      params: { q: query, format: "jsonv2", limit, countrycodes: "eg" },
    });
    return Array.isArray(data) ? data : [];
  } catch (err) {
    return [];
  }
};

const STATUS_COLORS = {
  open: "blue",
  assigned: "cyan",
  pending: "orange",
  inProgress: "gold",
  completed: "green",
  closed: "default",
  cancelled: "red",
};

export default function EditCallCenterFormBody({ editData }) {
  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    setFieldValue,
    setFieldTouched,
  } = useFormikContext();

  const [showIssueModal, setShowIssueModal] = useState(false);
  const [showSparePartModal, setShowSparePartModal] = useState(false);
  const [showVillageModal, setShowVillageModal] = useState(false);
  // The currently selected village's display name — seeded from the edit
  // fetch, updated when the agent picks a different one or adds a new one.
  const [externalVillage, setExternalVillage] = useState(null); // { id, name } | null — name already localized by the API
  // Undefined until a cascade step (or manual click) resolves a location —
  // CallCenterMapPicker falls back to its own default zoom until then.
  const [mapZoom, setMapZoom] = useState();

  const lang = localStorage.getItem("lang") === "ar" ? "ar" : "en";

  const editableFields = editData?.editableFields || {};
  const canEdit = !!editData?.canEdit;
  const isRestricted = !!editData?.isRestricted;

  useEffect(() => {
    setExternalVillage(
      editData?.customer?.villageId
        ? { id: editData.customer.villageId, name: editData.customer.villageName }
        : null,
    );
  }, [editData?.customer?.villageId, editData?.customer?.villageName]);

  const colorOptions = SERVICE_REQUEST_COLORS.map((c) => {
    const label = getLocalizedColorLabel(c.value, lang);
    return {
      label: (
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <span
            style={{
              display: "inline-block",
              width: "16px",
              height: "16px",
              borderRadius: "50%",
              backgroundColor: c.color,
              border: "1px solid #d9d9d9",
            }}
          />
          {label}
        </div>
      ),
      value: label,
    };
  });

  const { data: skusData } = useGetAllSkusQuery();
  const productOptions = (skusData?.data || []).map((s) => ({
    label: s.skuName,
    value: String(s.id),
  }));

  const { data: modelsData } = useGetModelsByProductQuery(values.productId, {
    skip: !values.productId,
  });
  const modelOptions = modelsData?.options || [];

  const { data: issuesData, refetch: refetchIssues } = useGetIssuesByModelQuery(
    { modelId: values.modelId, lang },
    { skip: !values.modelId },
  );
  const issueOptions = (issuesData?.data || []).map((issue) => ({
    label: issue.name,
    value: issue.id,
  }));

  const { data: sparePartsData, refetch: refetchSpareParts } = useGetSparePartsByIssuesQuery(
    { issueIds: values.defects, lang },
    { skip: !values.defects?.length },
  );
  const sparePartOptions = (sparePartsData?.data || []).map((part) => ({
    label: `${part.partCode} - ${part.name}`,
    value: part.id,
  }));

  const handleProductChange = (val) => {
    setFieldValue("productId", val);
    setFieldValue("modelId", "");
    setFieldValue("defects", []);
    setFieldValue("sparePartIds", []);
  };

  const handleModelChange = (val) => {
    setFieldValue("modelId", val);
    setFieldValue("defects", []);
    setFieldValue("sparePartIds", []);
  };

  // ** Governorate select — zooms/pins to the governorate center, same as the
  //    create form's cascade (CallCenterFormBody), so location stays in sync
  //    with the map even when only Governorate/Markaz/Village change here.
  const handleGovernorateChange = (val) => {
    setFieldValue("state", val);
    setFieldValue("city", "");
    setFieldValue("villageId", "");
    setExternalVillage(null);

    const center = getGovernorateCenter(val);
    if (center) {
      setFieldValue("latitude", center.lat);
      setFieldValue("longitude", center.lon);
      setFieldValue("address", `${val}, Egypt`);
      setMapZoom(ZOOM_GOVERNORATE);
    }
  };

  const markazOptions = useMemo(() => getMarkazOptions(values.state), [values.state]);

  const handleMarkazChange = (val) => {
    setFieldValue("city", val || "");
    setFieldValue("villageId", "");
    setExternalVillage(null);
    const selected = markazOptions.find((o) => o.value === val);
    if (selected) {
      setFieldValue("latitude", selected.lat);
      setFieldValue("longitude", selected.lon);
      setFieldValue("address", `${val}, ${values.state}, Egypt`);
      setMapZoom(ZOOM_MARKAZ);
    }
  };

  // ** Village pin — resolved once per selection (not per keystroke, since
  //    picking from a dropdown is a discrete action); a manual map click
  //    still always wins as the final, most precise override. Nominatim
  //    geocodes by name, not id, so this always takes the resolved name.
  const resolveVillagePin = async (villageName, city, governorate) => {
    if (!villageName || !governorate) return;
    const [first] = await forwardGeocode(
      `${villageName}, ${city || ""}, ${governorate}, Egypt`,
      1,
    );
    if (first) {
      setFieldValue("latitude", Number(first.lat));
      setFieldValue("longitude", Number(first.lon));
      setFieldValue("address", first.display_name || "");
      setMapZoom(ZOOM_VILLAGE);
    }
  };

  // ** Village options — same curated, per-(Governorate, Markaz) picklist as
  //    the create form. The API resolves the display name server-side from
  //    the request's language (Accept-Language header, see
  //    Redux/service/CallCenter/CallCenter.js), so `name` is already correct
  //    for the current UI language.
  const { data: villagesData, refetch: refetchVillages } = useGetVillagesByMarkazQuery(
    { governorate: values.state, markaz: values.city, lang },
    { skip: !values.state || !values.city },
  );
  const fetchedVillageOptions = (villagesData?.data || []).map((v) => ({
    label: v.name,
    value: v.id,
  }));
  // Preserve a village that isn't (yet, or no longer) in the curated
  // per-Markaz list (editData.customer's villageId predates the curated
  // list, or its Markaz was since corrected) instead of letting antd's
  // Select silently blank out an unmatched value.
  const villageOptions = useMemo(() => {
    if (
      values.villageId &&
      !fetchedVillageOptions.some((o) => String(o.value) === String(values.villageId))
    ) {
      const fallbackLabel =
        externalVillage && String(externalVillage.id) === String(values.villageId)
          ? externalVillage.name
          : String(values.villageId);
      return [...fetchedVillageOptions, { label: fallbackLabel, value: values.villageId }];
    }
    return fetchedVillageOptions;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [villagesData, values.villageId, externalVillage]);

  const handleVillageChange = (val) => {
    setFieldValue("villageId", val || "");
    const selected = (villagesData?.data || []).find((v) => String(v.id) === String(val));
    if (selected) {
      setExternalVillage({ id: selected.id, name: selected.name });
      resolveVillagePin(selected.name, values.city, values.state);
    }
  };

  const addressDisabled = !editableFields.address;
  const stateDisabled = !editableFields.state;
  const cityDisabled = !editableFields.city || !values.state;
  const villageDisabled = !editableFields.village || !values.city;
  const productDisabled = !editableFields.productId;
  const modelDisabled = !editableFields.modelId || !values.productId;
  const colorDisabled = !editableFields.color;
  const defectsDisabled = !editableFields.defects || !values.modelId;
  const sparePartsDisabled = !editableFields.defects || !values.defects?.length;
  const complaintDisabled = !editableFields.complaintDescription;
  const preferredTimeDisabled = !editableFields.preferredVisitTime;

  return (
    <div className="form-section">
      {!canEdit && editData?.readOnlyMessage && (
        <div className="alert alert-danger mb-3">
          <i className="fas fa-exclamation-triangle me-2"></i>
          <strong>{editData.readOnlyMessage}</strong>
        </div>
      )}
      {canEdit && isRestricted && (
        <div className="alert alert-warning mb-3">
          <i className="fas fa-info-circle me-2"></i>
          <strong>
            {trans(
              "This request is Assigned. Only address, contact and preferred visit time can be updated.",
            )}
          </strong>
        </div>
      )}

      <p className="fs-16 fw-500 brand-700 text-capitalize mb-2">
        {trans("Customer Information")}
      </p>
      <div className="row">
        <div className="col-lg-6">
          <Input
            title={trans("Customer Code")}
            type="text"
            id="customerCode"
            name="customerCode"
            formstyle="form-input"
            value={editData?.customer?.code || ""}
            disabled
          />
        </div>
        <div className="col-lg-6">
          <Input
            title={trans("Customer Name")}
            type="text"
            id="customerName"
            name="customerName"
            formstyle="form-input"
            value={editData?.customer?.name || ""}
            disabled
          />
        </div>
        <div className="col-lg-12 mt-2">
          <p className="fs-14 fw-500 brand-700 mb-2">{trans("Contact Number")}</p>
          <FieldArray name="secondaryPhones">
            {({ push, remove }) => {
              const rows = [
                { key: "landline", kind: "landline", value: values.landline || "" },
                ...(values.secondaryPhones || []).map((phone, index) => ({
                  key: `secondary-${index}`,
                  kind: "secondary",
                  index,
                  value: phone,
                })),
              ];
              const errorsByKey = {};
              const touchedByKey = {};
              (values.secondaryPhones || []).forEach((_, index) => {
                const key = `secondary-${index}`;
                errorsByKey[key] = Array.isArray(errors.secondaryPhones)
                  ? errors.secondaryPhones[index]
                  : undefined;
                touchedByKey[key] = Array.isArray(touched.secondaryPhones)
                  ? touched.secondaryPhones[index]
                  : touched.secondaryPhones;
              });

              const handleChangeRow = (key, value) => {
                if (key === "landline") {
                  setFieldValue("landline", value);
                } else {
                  setFieldValue(`secondaryPhones.${Number(key.split("-")[1])}`, value);
                }
              };
              const handleRemoveRow = (key) => {
                if (key === "landline") {
                  setFieldValue("landline", "");
                } else {
                  remove(Number(key.split("-")[1]));
                }
              };

              return (
                <ContactNumberList
                  primaryNumber={editData?.customer?.phoneNumber || ""}
                  matchedNumber={editData?.callerPhoneNumber ?? null}
                  rows={rows}
                  editable={editableFields.landline && editableFields.secondaryPhones}
                  onChangeRow={handleChangeRow}
                  onRemoveRow={handleRemoveRow}
                  onAddRow={() => push("")}
                  canAddMore={(values.secondaryPhones || []).length < MAX_SECONDARY_PHONES}
                  errorsByKey={errorsByKey}
                  touchedByKey={touchedByKey}
                />
              );
            }}
          </FieldArray>
        </div>
      </div>

      <p className="fs-16 fw-500 brand-700 text-capitalize mb-2 mt-3">
        {trans("Address Information")}
      </p>
      <div className="row">
        <div className="col-lg-6">
          <InputSelect
            id="state"
            name="state"
            label={trans("Governorate")}
            placeholder={trans("Select Governorate")}
            options={EgyptGovernorates}
            value={values.state || undefined}
            onChange={handleGovernorateChange}
            onBlur={() => setFieldTouched("state", true)}
            errors={errors.state}
            touched={touched.state}
            disabled={stateDisabled}
            formstyle="form-input"
          />
        </div>
        <div className="col-lg-6">
          <InputSelect
            id="city"
            name="city"
            label={trans("Markaz")}
            placeholder={trans(values.state ? "Select Markaz" : "Select Governorate first")}
            options={markazOptions}
            value={values.city || undefined}
            onChange={handleMarkazChange}
            onBlur={() => setFieldTouched("city", true)}
            errors={errors.city}
            touched={touched.city}
            disabled={cityDisabled}
            formstyle="form-input"
          />
        </div>
        <div className="col-lg-6">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <label className="form-label mb-0 text-capitalize">
              {trans("Village / Neighborhood")}
            </label>
            {values.city && !villageDisabled && (
              <Button
                type="link"
                size="small"
                icon={<PlusOutlined />}
                onClick={() => setShowVillageModal(true)}
              >
                {lang === "ar" ? "إضافة قرية جديدة" : "Add New Village"}
              </Button>
            )}
          </div>
          <InputSelect
            id="villageId"
            name="villageId"
            placeholder={trans(values.city ? "Select Village" : "Select Markaz first")}
            options={villageOptions}
            value={values.villageId || undefined}
            onChange={handleVillageChange}
            onBlur={() => setFieldTouched("villageId", true)}
            errors={errors.villageId}
            touched={touched.villageId}
            disabled={villageDisabled}
            formstyle="form-input"
          />
        </div>
        <div className="col-lg-12">
          <TextAreaInput
            title={trans("Detailed Address")}
            placeholder={trans("Detailed Address")}
            formstyle="form-input"
            id="address"
            name="address"
            value={values.address}
            onChange={handleChange}
            onBlur={handleBlur}
            errors={errors.address}
            touched={touched.address}
            disabled={addressDisabled}
            rows={2}
          />
        </div>
      </div>

      <CallCenterMapPicker
        latitude={values.latitude}
        longitude={values.longitude}
        zoom={mapZoom}
        address={values.address}
        disabled={addressDisabled}
        onPick={({ latitude, longitude, address }) => {
          setFieldValue("latitude", latitude);
          setFieldValue("longitude", longitude);
          // Address always reflects the exact pinned location, same as the create form.
          setFieldValue("address", address || "");
        }}
      />

      <p className="fs-16 fw-500 brand-700 text-capitalize mb-2 mt-3">
        {trans("Service Request Information")}
      </p>
      <div className="row">
        <div className="col-lg-6">
          <Input
            title={trans("Request Number")}
            type="text"
            id="requestNumber"
            name="requestNumber"
            formstyle="form-input"
            value={editData?.code || ""}
            disabled
          />
        </div>
        <div className="col-lg-6">
          <label className="form-label d-block">{trans("Status")}</label>
          <Tag color={STATUS_COLORS[editData?.status] || "default"} className="text-capitalize">
            {trans(editData?.status)}
          </Tag>
        </div>
        <div className="col-lg-6">
          <InputSelect
            id="productId"
            name="productId"
            label={trans("Product")}
            placeholder={trans("Select Product")}
            options={productOptions}
            value={values.productId || undefined}
            onChange={handleProductChange}
            onBlur={() => setFieldTouched("productId", true)}
            errors={errors.productId}
            touched={touched.productId}
            disabled={productDisabled}
            formstyle="form-input"
          />
        </div>
        <div className="col-lg-6">
          <InputSelect
            id="modelId"
            name="modelId"
            label={trans("Model")}
            placeholder={trans("Select Model")}
            options={modelOptions}
            value={values.modelId || undefined}
            onChange={handleModelChange}
            disabled={modelDisabled}
            formstyle="form-input"
          />
        </div>
        <div className="col-lg-6">
          <label className="form-label">{lang === "ar" ? "لون المنتج" : "Model Color"}</label>
          <Select
            id="color"
            name="color"
            placeholder={trans("Select Color")}
            options={colorOptions}
            value={values.color || undefined}
            onChange={(val) => setFieldValue("color", val)}
            disabled={colorDisabled}
            style={{ width: "100%" }}
          />
        </div>
        <div className="col-lg-12">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <label className="form-label mb-0">{lang === "ar" ? "عيوب / مشاكل" : "Defects / Issues"}</label>
            {values.modelId && !defectsDisabled && (
              <Button
                type="link"
                size="small"
                icon={<PlusOutlined />}
                onClick={() => setShowIssueModal(true)}
              >
                {lang === "ar" ? "إضافة عيب جديد" : "Add New Issue"}
              </Button>
            )}
          </div>
          <InputSelect
            id="defects"
            name="defects"
            placeholder={
              lang === "ar"
                ? values.modelId
                  ? "اختر العيوب"
                  : "اختر الموديل أولاً"
                : values.modelId
                  ? "Select Defects"
                  : "Select Model first"
            }
            mode="multiple"
            options={issueOptions}
            value={values.defects || undefined}
            onChange={(val) => {
              setFieldValue("defects", val || []);
              setFieldValue("sparePartIds", []);
            }}
            disabled={defectsDisabled}
            formstyle="form-input"
          />
        </div>
        <div className="col-lg-12">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <label className="form-label mb-0">{lang === "ar" ? "قطع غيار" : "Spare Parts"}</label>
            {values.defects?.length === 1 && !sparePartsDisabled && (
              <Button
                type="link"
                size="small"
                icon={<PlusOutlined />}
                onClick={() => setShowSparePartModal(true)}
              >
                {lang === "ar" ? "إضافة قطعة غيار جديدة" : "Add New Spare Part"}
              </Button>
            )}
          </div>
          <InputSelect
            id="sparePartIds"
            name="sparePartIds"
            placeholder={
              lang === "ar"
                ? values.defects?.length
                  ? "اختر قطع الغيار"
                  : "اختر العيوب أولاً"
                : values.defects?.length
                  ? "Select Spare Parts"
                  : "Select Defects first"
            }
            mode="multiple"
            options={sparePartOptions}
            value={values.sparePartIds || undefined}
            onChange={(val) => setFieldValue("sparePartIds", val || [])}
            disabled={sparePartsDisabled}
            formstyle="form-input"
          />
        </div>
        <div className="col-lg-12">
          <DatePickerInput
            title={`${trans("Preferred Visit Time")} (${trans("optional")})`}
            formstyle="form-input"
            disabled={preferredTimeDisabled}
            errors={errors.preferredVisitTime}
            touched={touched.preferredVisitTime}
            onChange={({ startDate, endDate }) => {
              setFieldValue("preferredVisitTime", startDate ? startDate.format(DATE_FORMAT) : "");
              setFieldValue("preferredVisitTimeEnd", endDate ? endDate.format(DATE_FORMAT) : "");
              setFieldTouched("preferredVisitTime", true);
            }}
            value={
              values.preferredVisitTime && values.preferredVisitTimeEnd
                ? [values.preferredVisitTime, values.preferredVisitTimeEnd]
                : null
            }
          />
        </div>
        <div className="col-lg-12">
          <TextAreaInput
            title={trans("Complaint Description")}
            placeholder={trans("Write complaint description")}
            formstyle="form-input"
            id="complaintDescription"
            name="complaintDescription"
            value={values.complaintDescription}
            onChange={handleChange}
            onBlur={handleBlur}
            errors={errors.complaintDescription}
            touched={touched.complaintDescription}
            disabled={complaintDisabled}
            maxLength={COMPLAINT_DESCRIPTION_MAX_LENGTH}
          />
        </div>
      </div>

      <CreateIssueModal
        visible={showIssueModal}
        onClose={() => setShowIssueModal(false)}
        modelId={values.modelId}
        onSuccess={async (newIssue) => {
          await refetchIssues();
          setFieldValue("defects", [...(values.defects || []), newIssue.id]);
        }}
      />

      <CreateSparePartModal
        visible={showSparePartModal}
        onClose={() => setShowSparePartModal(false)}
        issueId={values.defects?.[0]}
        onSuccess={async (newSparePart) => {
          await refetchSpareParts();
          setFieldValue("sparePartIds", [...(values.sparePartIds || []), newSparePart.id]);
        }}
      />

      <CreateVillageModal
        visible={showVillageModal}
        onClose={() => setShowVillageModal(false)}
        governorate={values.state}
        markaz={values.city}
        onSuccess={async (newVillage) => {
          await refetchVillages();
          setFieldValue("villageId", newVillage.id);
          setExternalVillage({ id: newVillage.id, name: newVillage.name });
          resolveVillagePin(newVillage.name, values.city, values.state);
        }}
      />
    </div>
  );
}
